import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Info, LogOut, Copy, ArrowDownCircle, ArrowUpCircle, ShoppingCart, Users, TrendingUp, Download, MessageCircle, X, Eye, EyeOff, Lock, Pickaxe, Disc3, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { API, LOGO_URL } from "../config/constants";
import LanguageSelector from "../components/LanguageSelector";
import BottomNav from "../components/BottomNav";
import AboutModal from "../components/AboutModal";
import WalletModal from "../components/WalletModal";
import LiveTransactionTicker from "../components/LiveTransactionTicker";

// Helper function to make URLs clickable in text
const LinkifyText = ({ text }) => {
  if (!text) return null;
  
  // Regex to match URLs (http, https, telegram, youtube, etc.)
  const urlRegex = /(https?:\/\/[^\s]+|t\.me\/[^\s]+|telegram\.me\/[^\s]+|youtu\.be\/[^\s]+|youtube\.com\/[^\s]+)/gi;
  
  const parts = text.split(urlRegex);
  const matches = text.match(urlRegex) || [];
  
  return (
    <>
      {parts.map((part, index) => {
        // Check if this part is a URL
        const isUrl = matches.includes(part);
        if (isUrl) {
          // Add https:// if it's a telegram link without protocol
          let href = part;
          if (part.startsWith('t.me/') || part.startsWith('telegram.me/')) {
            href = 'https://' + part;
          }
          return (
            <a
              key={index}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:text-yellow-300 break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
};

const DashboardPage = () => {
  const { user, refreshUser, logout, authAxios } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [liveBalance, setLiveBalance] = useState({ usdt: 0, ltg: 0 });
  const [announcement, setAnnouncement] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [promoCode, setPromoCode] = useState("");
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  const [unreadSupportMessages, setUnreadSupportMessages] = useState(0);
  const [miningReminders, setMiningReminders] = useState(0);
  // Regulatory compliance notice — shows once per login session
  const [showRegulationNotice, setShowRegulationNotice] = useState(false);
  
  // PIN Change Modal States
  const [showPinChange, setShowPinChange] = useState(false);
  const [pinChangeTab, setPinChangeTab] = useState("login"); // "login" or "withdrawal"
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [pinChangeLoading, setPinChangeLoading] = useState(false);

  // Fetch unread support messages count
  useEffect(() => {
    if (!user?.id) return;
    const fetchSupportUnread = async () => {
      try {
        const api = authAxios();
        const chatRes = await api.get(`/support/chat/user/${user.id}`);
        if (chatRes.data?.unread_user) {
          setUnreadSupportMessages(chatRes.data.unread_user);
        } else {
          setUnreadSupportMessages(0);
        }
      } catch (e) { 
        setUnreadSupportMessages(0);
      }
    };
    fetchSupportUnread();
    const interval = setInterval(fetchSupportUnread, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, [user?.id, authAxios]);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const res = await axios.get(`${API}/announcement/active`);
        if (res.data) {
          const dismissedId = localStorage.getItem('dismissed_announcement');
          if (dismissedId !== res.data.id) {
            setAnnouncement(res.data);
            setShowAnnouncement(true);
          }
        }
      } catch (e) { console.error(e); }
    };
    fetchAnnouncement();
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const fetchNotifications = async () => {
      try {
        const api = authAxios();
        const [notifRes, countRes] = await Promise.all([
          api.get(`/notifications/${user.id}`),
          api.get(`/notifications/${user.id}/unread-count`)
        ]);
        setNotifications(notifRes.data);
        setUnreadCount(countRes.data.count);
      } catch (e) { console.error(e); }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user?.id, authAxios]);

  const markNotificationsRead = async () => {
    try {
      const api = authAxios();
      await api.put(`/notifications/${user.id}/mark-read`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({...n, is_read: true})));
    } catch (e) { console.error(e); }
  };

  const redeemPromoCode = async () => {
    if (!promoCode) { toast.error("Enter promo code"); return; }
    setRedeemingPromo(true);
    try {
      const api = authAxios();
      const res = await api.post(`/promo/redeem/${user.id}`, { code: promoCode });
      toast.success(res.data.message);
      setPromoCode("");
      refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid promo code"); }
    setRedeemingPromo(false);
  };

  const dismissAnnouncement = () => {
    if (announcement) {
      localStorage.setItem('dismissed_announcement', announcement.id);
    }
    setShowAnnouncement(false);
  };

  // Sync live balance with server data when user changes (login/refresh)
  // The backend already calculates and stores accumulated rewards
  // We only need to show the live counter animation between refreshes
  useEffect(() => {
    if (!user) return;
    // Main USDT = Total (deposited + earning)
    // Main LTG = ONLY Earning LTG (staked_reward), NOT deposited LTG
    setLiveBalance({ usdt: user.usdt_balance, ltg: user.staked_reward || 0 });
  }, [user?.usdt_balance, user?.staked_reward]);

  // Live counter animation - only for visual effect between server refreshes
  useEffect(() => {
    if (!user || user.staked_amount <= 0) return;
    
    const interval = setInterval(() => {
      const rewardPerSecond = (user.staked_amount * 0.01) / 86400;
      setLiveBalance(prev => ({
        usdt: prev.usdt + (rewardPerSecond / 1000),
        ltg: prev.ltg + rewardPerSecond
      }));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [user?.staked_amount]);

  useEffect(() => {
    refreshUser();
    const interval = setInterval(refreshUser, 30000);
    return () => clearInterval(interval);
  }, []);

  // Poll mining reminder badge for the dashboard Mine button blink
  useEffect(() => {
    if (!user) return;
    const fetchReminders = async () => {
      try {
        const res = await authAxios().get("/mining/status");
        setMiningReminders(res.data?.unread_reminders || 0);
      } catch (e) { /* silent */ }
    };
    fetchReminders();
    const iv = setInterval(fetchReminders, 30000);
    return () => clearInterval(iv);
  }, [user?.id]);

  // Show regulatory compliance notice once per login session per user
  useEffect(() => {
    if (!user?.id) return;
    const key = `reg_notice_seen_${user.id}`;
    if (!sessionStorage.getItem(key)) {
      setShowRegulationNotice(true);
    }
  }, [user?.id]);

  const dismissRegulationNotice = () => {
    if (user?.id) {
      sessionStorage.setItem(`reg_notice_seen_${user.id}`, "1");
    }
    setShowRegulationNotice(false);
  };

  if (!user) return null;

  const actions = [
    { icon: ArrowDownCircle, label: t("deposit"), color: "#22c55e", modal: "deposit" },
    { icon: ArrowUpCircle, label: t("withdraw"), color: "#ef4444", modal: "withdraw" },
    { icon: ShoppingCart, label: t("buy"), color: "#3b82f6", modal: "buy" },
    { icon: Disc3, label: "Spin", color: "#a855f7", modal: "spin" },
    { icon: TrendingUp, label: t("stake"), color: "#f59e0b", modal: "stake" },
    { icon: Pickaxe, label: "Mine", color: "#10b981", modal: "mine" },
  ];

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied!");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Copied!");
      }
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  // Handle PIN Change
  const handlePinChange = async () => {
    if (!currentPin) {
      toast.error("Enter current PIN");
      return;
    }
    if (!newPin || !confirmPin) {
      toast.error("Enter new PIN and confirm");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("PINs don't match");
      return;
    }
    
    if (pinChangeTab === "login") {
      if (newPin.length !== 8 || !/^\d+$/.test(newPin)) {
        toast.error("Login PIN must be 8 digits");
        return;
      }
    } else {
      if (newPin.length !== 6 || !/^\d+$/.test(newPin)) {
        toast.error("Withdrawal PIN must be 6 digits");
        return;
      }
    }
    
    setPinChangeLoading(true);
    try {
      const api = authAxios();
      await api.post(`/user/change-pin`, {
        current_pin: currentPin,
        new_pin: newPin,
        pin_type: pinChangeTab
      });
      toast.success("PIN changed! Please login again.");
      setShowPinChange(false);
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change PIN");
    } finally {
      setPinChangeLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="dashboard-page">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setShowPinChange(true)} data-testid="logo-pin-change">
          <div className="relative">
            <img 
              src={LOGO_URL} 
              alt="Logo" 
              className={`w-10 h-10 rounded-full transition-all ${user?.pin_updated !== true ? 'ring-2 ring-red-500 animate-pulse' : 'hover:ring-2 hover:ring-primary'}`} 
            />
            {user?.pin_updated !== true && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
            )}
          </div>
          <span className="text-gold-gradient font-manrope font-bold">Lite Gold</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowNotifications(true); markNotificationsRead(); }} className="p-2 text-muted-foreground hover:text-primary relative">
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button onClick={() => setShowAbout(true)} className="p-2 text-muted-foreground hover:text-primary"><Info size={20} /></button>
          <LanguageSelector />
          <button onClick={() => { logout(); navigate("/"); }} className="p-2 text-muted-foreground hover:text-red-500" data-testid="logout-btn"><LogOut size={20} /></button>
        </div>
      </div>

      {/* Welcome */}
      <p className="px-4 text-muted-foreground">{t("welcome")}, {user.full_name}</p>

      {/* Total Deposited USDT - Premium Card with Activation Status */}
      <div className="mx-4 mt-3 relative overflow-hidden rounded-xl bg-gradient-to-r from-green-900/40 via-emerald-900/30 to-teal-900/40 border border-green-500/30 p-3">
        <div className="absolute top-0 right-0 w-24 h-24 bg-green-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="relative z-10">
          {/* Account Activation Status - At Top */}
          {user.is_activated ? (
            <div className="mb-2 p-1.5 bg-green-500/20 border border-green-500/40 rounded-lg text-center">
              <p className="text-green-400 text-xs font-semibold">✓ Account Activated</p>
            </div>
          ) : (
            <div className="mb-2 p-1.5 bg-red-500/20 border border-red-500/40 rounded-lg text-center">
              <p className="text-red-400 text-xs font-semibold">Account Not Activated - Deposit ${(user.activation_required || 50).toFixed(0)} to unlock Withdraw & Send</p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shadow-lg ${user.total_deposited_usdt > 0 ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/30' : 'bg-gradient-to-br from-gray-500 to-gray-600 shadow-gray-500/30'}`}>
                <span className="text-lg">{user.total_deposited_usdt > 0 ? '✓' : '💰'}</span>
              </div>
              <div>
                <p className={`text-xs font-medium ${user.total_deposited_usdt > 0 ? 'text-green-400' : 'text-gray-400'}`}>Total Deposited</p>
                <p className="text-white font-manrope text-2xl font-bold">${(user.total_deposited_usdt || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
              </div>
            </div>
            <div className="text-right">
              {user.total_deposited_usdt > 0 ? (
                <>
                  <div className="bg-green-500/20 border border-green-500/30 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-green-400 text-[10px] font-bold">VERIFIED</span>
                  </div>
                  <p className="text-emerald-400 text-[10px] mt-0.5">Earning 1% Daily</p>
                </>
              ) : (
                <>
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                    <span className="text-yellow-400 text-[10px] font-bold">DEPOSIT NOW</span>
                  </div>
                  <p className="text-yellow-400 text-[10px] mt-0.5">Start Earning 1% Daily</p>
                </>
              )}
            </div>
          </div>
          
          <div className="mt-2 pt-2 border-t border-green-500/20 flex justify-between text-xs">
            <div>
              <span className="text-muted-foreground">Staked: </span>
              <span className="text-primary font-bold">{(user.staked_amount || 0).toLocaleString()} LTG</span>
            </div>
            <div>
              <span className="text-muted-foreground">≈ </span>
              <span className="text-yellow-400 font-bold">${((user.staked_amount || 0) / 1000).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Card */}
      <div className="mx-4 mt-4 balance-card">
        <div className="flex justify-between items-start">
          <div className="flex-1 text-center">
            <p className="text-muted-foreground text-sm">USDT</p>
            <p className="font-manrope text-3xl font-bold tabular-nums">${liveBalance.usdt.toFixed(4)}</p>
            {user.staked_amount > 0 && <p className="text-green-500 text-xs animate-pulse">+${((user.staked_amount * 0.01 / 86400) / 1000).toFixed(8)}/s</p>}
          </div>
          <div className="w-px h-16 bg-border mx-4"></div>
          <div className="flex-1 text-center">
            <p className="text-muted-foreground text-sm">LTG</p>
            <p className="font-manrope text-3xl font-bold text-primary tabular-nums">{liveBalance.ltg.toFixed(2)}</p>
            {user.staked_amount > 0 && <p className="text-green-500 text-xs animate-pulse">+{(user.staked_amount * 0.01 / 86400).toFixed(6)}/s</p>}
          </div>
        </div>

        {/* Deposited Balances */}
        <div className="mt-3 flex gap-2">
          <div className="flex-1 p-2 bg-black/20 border border-border rounded-lg text-center">
            <p className="text-muted-foreground text-xs">💵 Deposited USDT</p>
            <p className="font-bold text-green-400">${(user.deposited_usdt || 0).toFixed(2)}</p>
          </div>
          <div className="flex-1 p-2 bg-black/20 border border-border rounded-lg text-center">
            <p className="text-muted-foreground text-xs">💰 Deposited LTG</p>
            <p className="font-bold text-primary">{(user.sellable_ltg || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Wallet Address */}
        <div className="mt-4 p-3 bg-black/30 rounded-lg flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-xs">{t("walletAddress")}</p>
            <p className="text-primary text-sm font-mono">{user.wallet_address}</p>
          </div>
          <button onClick={() => copyToClipboard(user.wallet_address)} className="p-2 hover:bg-white/10 rounded">
            <Copy size={18} className="text-primary" />
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-grid mx-4 mt-6">
        {actions.map((action) => {
          const blink = action.modal === "mine" && miningReminders > 0;
          const isVipP2P = false;
          const isSpin = action.modal === "spin";
          return (
            <button
              key={action.label}
              onClick={() => action.modal === "stake" ? navigate("/stake") : action.modal === "mine" ? navigate("/mine") : action.modal === "spin" ? navigate("/spin") : setModal(action.modal)}
              className={`action-btn relative ${blink ? "ring-2 ring-red-500 animate-pulse rounded-xl" : ""}`}
              data-testid={`action-${action.modal}`}
            >
              {isSpin ? (
                <span className="relative inline-flex items-center justify-center text-3xl spin-fun-emoji" role="img" aria-label="Spin wheel">
                  🎡
                  <span className="absolute -top-2 -right-2 text-sm animate-bounce">✨</span>
                </span>
              ) : (
                <action.icon size={28} color={action.color} />
              )}
              <span className="text-sm mt-2 text-muted-foreground">{action.label}</span>
              {blink && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="mine-reminder-badge">
                  {miningReminders > 9 ? "9+" : miningReminders}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Live Transaction Ticker - Above Stake */}
      <LiveTransactionTicker />

      {/* Stake Banner */}
      {user.staked_amount > 0 && (
        <div className="mx-4 mt-6 stake-banner flex items-center justify-between cursor-pointer" onClick={() => navigate("/stake")}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-green-400 text-sm">
              Stake: {user.staked_amount.toFixed(0)} LTG
            </span>
          </div>
          <span className="text-yellow-400 text-sm font-semibold">
            (${(user.staked_amount / 1000).toFixed(2)} USDT) • 1% Daily
          </span>
        </div>
      )}

      {/* Promo Code Section */}
      <div className="mx-4 mt-4 p-3 bg-card/50 border border-border rounded-xl">
        <div className="flex items-center gap-2">
          <input 
            type="text" 
            placeholder="🎁 Enter Promo Code" 
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
          />
          <button 
            onClick={redeemPromoCode}
            disabled={redeemingPromo || !promoCode}
            className="px-4 py-1.5 bg-green-600 rounded-full text-sm font-bold disabled:opacity-50"
          >
            {redeemingPromo ? "..." : "Redeem"}
          </button>
        </div>
      </div>

      {/* Modals */}
      {modal && <WalletModal type={modal} onClose={() => setModal(null)} user={user} refreshUser={refreshUser} t={t} />}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {/* Floating Support Chat Button */}
      <button
        onClick={() => navigate("/support")}
        className="fixed bottom-20 right-4 z-40 bg-black border-2 border-yellow-500 p-2 rounded-full shadow-lg hover:scale-105 transition-transform"
        title={t("supportChat")}
      >
        <MessageCircle size={16} className="text-yellow-500" />
        {unreadSupportMessages > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold animate-pulse">
            {unreadSupportMessages > 9 ? '9+' : unreadSupportMessages}
          </span>
        )}
      </button>

      {/* Announcement Modal */}
      {showAnnouncement && announcement && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 via-card to-pink-900 border border-purple-500/30 rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-4xl animate-bounce">📢</span>
              <h2 className="font-manrope font-bold text-2xl text-center bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {announcement.title}
              </h2>
            </div>
            <div className="bg-black/30 rounded-xl p-4 mb-6 max-h-[300px] overflow-y-auto">
              <p className="text-white whitespace-pre-wrap leading-relaxed">
                <LinkifyText text={announcement.message} />
              </p>
            </div>
            <button 
              onClick={dismissAnnouncement}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-bold hover:from-purple-500 hover:to-pink-500 transition-all"
            >
              ✓ Got it!
            </button>
          </div>
        </div>
      )}

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl p-4 w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-manrope font-bold text-xl flex items-center gap-2">🔔 Notifications</h2>
              <button onClick={() => setShowNotifications(false)}><X size={24} /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3">
              {notifications.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No notifications yet</p>
              ) : (
                notifications.map(notif => (
                  <div key={notif.id} className={`p-3 rounded-xl border ${notif.is_read ? 'bg-card border-border' : 'bg-primary/10 border-primary/30'}`}>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">
                        {notif.notification_type === 'success' && '✅'}
                        {notif.notification_type === 'warning' && '⚠️'}
                        {notif.notification_type === 'promo' && '🎁'}
                        {notif.notification_type === 'info' && 'ℹ️'}
                      </span>
                      <div className="flex-1">
                        <p className="font-bold">{notif.title}</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          <LinkifyText text={notif.message} />
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* PIN Change Modal */}
      {showPinChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowPinChange(false)}></div>
          <div className="relative bg-card rounded-2xl p-6 w-[90%] max-w-md border border-border z-10">
            <button onClick={() => setShowPinChange(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-white">
              <X size={24} />
            </button>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Lock className="text-primary" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Change PIN</h2>
                <p className="text-sm text-muted-foreground">Update your security</p>
              </div>
            </div>
            
            {/* Tab Selection */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => { setPinChangeTab("login"); setCurrentPin(""); setNewPin(""); setConfirmPin(""); }}
                className={`flex-1 py-2 rounded-lg font-medium transition-all ${pinChangeTab === "login" ? "bg-primary text-black" : "bg-card border border-border"}`}
              >
                Login PIN (8-digit)
              </button>
              <button
                onClick={() => { setPinChangeTab("withdrawal"); setCurrentPin(""); setNewPin(""); setConfirmPin(""); }}
                className={`flex-1 py-2 rounded-lg font-medium transition-all ${pinChangeTab === "withdrawal" ? "bg-primary text-black" : "bg-card border border-border"}`}
              >
                Withdrawal PIN (6-digit)
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Current PIN */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Current {pinChangeTab === "login" ? "Login" : "Withdrawal"} PIN</label>
                <div className="relative">
                  <input
                    type={showCurrentPin ? "text" : "password"}
                    value={currentPin}
                    onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, pinChangeTab === "login" ? 8 : 6))}
                    placeholder={pinChangeTab === "login" ? "Enter 8-digit PIN" : "Enter 6-digit PIN"}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 pr-12"
                    maxLength={pinChangeTab === "login" ? 8 : 6}
                  />
                  <button onClick={() => setShowCurrentPin(!showCurrentPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showCurrentPin ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              {/* New PIN */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">New {pinChangeTab === "login" ? "Login" : "Withdrawal"} PIN</label>
                <div className="relative">
                  <input
                    type={showNewPin ? "text" : "password"}
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, pinChangeTab === "login" ? 8 : 6))}
                    placeholder={pinChangeTab === "login" ? "Enter new 8-digit PIN" : "Enter new 6-digit PIN"}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 pr-12"
                    maxLength={pinChangeTab === "login" ? 8 : 6}
                  />
                  <button onClick={() => setShowNewPin(!showNewPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showNewPin ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              {/* Confirm PIN */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Confirm New PIN</label>
                <input
                  type="password"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, pinChangeTab === "login" ? 8 : 6))}
                  placeholder="Confirm new PIN"
                  className="w-full bg-background border border-border rounded-lg px-4 py-3"
                  maxLength={pinChangeTab === "login" ? 8 : 6}
                />
              </div>
              
              <button
                onClick={handlePinChange}
                disabled={pinChangeLoading}
                className="w-full bg-primary text-black font-bold py-3 rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50"
              >
                {pinChangeLoading ? "Changing..." : "Change PIN"}
              </button>
              
              <p className="text-xs text-muted-foreground text-center">
                After changing PIN, you will be logged out from all devices
              </p>
            </div>
          </div>
        </div>
      )}

      <BottomNav />

      {/* Regulatory Compliance Notice — shows once per login session */}
      {showRegulationNotice && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
          onClick={dismissRegulationNotice}
          data-testid="regulation-notice-modal"
        >
          <div
            className="w-full max-w-md max-h-[92vh] flex flex-col bg-gradient-to-br from-slate-900 to-slate-950 border border-yellow-500/30 rounded-2xl overflow-hidden shadow-2xl shadow-yellow-500/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500/15 to-amber-500/10 border-b border-yellow-500/20 px-5 py-3.5 flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shrink-0 shadow-lg shadow-yellow-500/40">
                <ShieldCheck size={20} className="text-slate-900" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-base font-bold text-white tracking-tight">Important Notice</div>
                <div className="text-[11px] text-yellow-300/80">Regulatory Compliance Update</div>
              </div>
            </div>

            {/* Body (scrollable) */}
            <div className="px-5 py-4 space-y-2.5 text-[13px] leading-relaxed overflow-y-auto">
              <p className="text-slate-200 font-medium">
                Dear Valued User,
              </p>
              <p className="text-slate-300">
                Our team is actively carrying out all
                <span className="text-yellow-300 font-semibold"> required legal and regulatory work </span>
                in line with government guidelines and global regulatory standards.
              </p>
              <p className="text-slate-300">
                Every necessary document, licence and compliance measure is being actively pursued and implemented with due diligence to keep our platform
                <span className="text-white font-medium"> safe, transparent and fully lawful </span>
                for all users.
              </p>
              <p className="text-slate-300">
                Fresh updates and new features will be rolled out shortly. We appreciate your patience and continued trust in our service.
              </p>
              <p className="text-slate-400 text-[12px] pt-1 border-t border-white/5 mt-3">
                — Compliance & Operations Team
              </p>
            </div>

            {/* Footer (sticky at bottom) */}
            <div className="px-5 py-4 border-t border-white/5 bg-slate-950/70 shrink-0">
              <button
                onClick={dismissRegulationNotice}
                className="w-full py-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-slate-900 rounded-xl font-bold text-sm transition shadow-lg shadow-yellow-500/30"
                data-testid="regulation-notice-ok"
              >
                I Understand — Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
